import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Loader2, CheckCircle2, XCircle } from 'lucide-react';
import api from '../../api/axios';
import useSEO from '../../components/SEO';

const VerifyEmail = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const navigate = useNavigate();
    const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying');
    const [message, setMessage] = useState('');
    const verificationAttempted = useRef(false);

    useSEO({
        title: "Vérification d'email - ISTMECH",
        description: "Vérifiez votre adresse email pour activer votre compte.",
        url: "https://www.istmech.edu.ht/verify-email",
        robots: "noindex, nofollow"
    });

    useEffect(() => {
        if (!token) {
            setStatus('error');
            setMessage("Jeton de vérification manquant.");
            return;
        }

        if (verificationAttempted.current) return;
        verificationAttempted.current = true;

        const verifyToken = async () => {
            try {
                // Determine if we are authenticated to update local state if needed
                // But this is a public verification usually.
                await api.get(`/auth/verify-email?token=${token}`);
                setStatus('success');
                setMessage("Votre email a été vérifié avec succès !");

                // Optional: Auto-redirect after 5 seconds
                setTimeout(() => navigate('/login'), 5000);
            } catch (error: any) {
                console.error("Verification failed", error);
                setStatus('error');
                setMessage(error.response?.data?.message || "Échec de la vérification. Le lien est peut-être expiré ou invalide.");
            }
        };

        verifyToken();
    }, [token, navigate]);

    return (
        <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900 p-4">
            <Card className="w-full max-w-md shadow-lg">
                <CardHeader className="text-center">
                    <CardTitle className="text-2xl font-bold">Vérification d'Email</CardTitle>
                    <CardDescription>Activation de votre compte ISTMECH</CardDescription>
                </CardHeader>
                <CardContent className="flex flex-col items-center justify-center py-6 text-center space-y-4">
                    {status === 'verifying' && (
                        <>
                            <Loader2 className="h-16 w-16 text-blue-600 animate-spin" />
                            <p className="text-lg font-medium text-gray-700 dark:text-gray-300">Vérification en cours...</p>
                        </>
                    )}

                    {status === 'success' && (
                        <>
                            <CheckCircle2 className="h-16 w-16 text-green-500" />
                            <h3 className="text-xl font-semibold text-green-700 dark:text-green-400">Succès !</h3>
                            <p className="text-gray-600 dark:text-gray-300">{message}</p>
                        </>
                    )}

                    {status === 'error' && (
                        <>
                            <XCircle className="h-16 w-16 text-red-500" />
                            <h3 className="text-xl font-semibold text-red-700 dark:text-red-400">Erreur</h3>
                            <p className="text-gray-600 dark:text-gray-300">{message}</p>
                        </>
                    )}
                </CardContent>
                <CardFooter className="flex justify-center">
                    <Button
                        onClick={() => navigate('/login')}
                        className={status === 'success' ? "bg-green-600 hover:bg-green-700" : ""}
                        variant={status === 'error' ? "destructive" : "default"}
                    >
                        {status === 'success' ? "Se connecter maintenant" : "Retour à la connexion"}
                    </Button>
                </CardFooter>
            </Card>
        </div>
    );
};

export default VerifyEmail;
